import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type ModuleConfigState, type ModuleId, type ModuleResult } from '@pixacare/forms';
import { ModuleResolverInputs } from '../../models/module-resolver-inputs';
import { ModuleResolverService } from '../../services/module-resolver.service';

/**
 * Headless host for one engine-published `AUTO` module.
 *
 * Rendered once per auto {@link ModuleConfigState} at the form root (so it is not
 * tied to the active section), it resolves the module through the same
 * `ModuleResolverService` a manual trigger uses, then emits the result up. When the
 * engine removes the auto module (e.g. after the result is applied), the host
 * unmounts and any in-flight resolution is cancelled via `takeUntilDestroyed`.
 */
@Component({
  selector: 'pxc-form-auto-trigger',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormAutoTriggerComponent implements OnInit {
  readonly autoModule = input.required<ModuleConfigState>();
  readonly moduleInputs = input.required<ModuleResolverInputs>();

  readonly moduleTriggered = output<{ moduleId: ModuleId; targetId: string; result: ModuleResult }>();

  private readonly moduleResolver = inject(ModuleResolverService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const { moduleId, targetId } = this.autoModule();
    this.moduleResolver
      .resolve(moduleId, this.moduleInputs())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.moduleTriggered.emit({ moduleId, targetId, result }));
  }
}
